import { firestore } from '../../settings/firebase';
import { collection, doc, getDoc, getDocs, onSnapshot, query } from 'firebase/firestore';
import { StateCreator } from 'zustand';
import { IndexTranslation, TranslationsStore } from './store';

const COLLECTION_NAME = 'translations';

let globalUnsubscribe: (() => void) | undefined;

export interface TranslationSlice extends TranslationsStore {
  fetchTranslations: () => Promise<void>;
  getTranslation: (indexName: string) => Promise<IndexTranslation | null>;
  subscribeToTranslations: () => () => void;
  setCurrentTranslation: (translation: IndexTranslation | null) => void;
}

export const createTranslationStore: StateCreator<TranslationSlice> = (set, get) => ({
  translations: {},
  loading: false,
  error: null,
  currentTranslation: null,

  fetchTranslations: async () => {
    // Clean up existing subscription if any
    if (globalUnsubscribe) {
      globalUnsubscribe();
    }

    set({ loading: true, error: null });
    try {
      const q = query(collection(firestore, COLLECTION_NAME));

      // Set up real-time listener
      globalUnsubscribe = onSnapshot(
        q,
        querySnapshot => {
          const translations: Record<string, IndexTranslation> = {};
          querySnapshot.forEach(doc => {
            const data = doc.data();
            if (data) {
              translations[doc.id] = {
                indexName: doc.id,
                en: data.en,
                fr: data.fr,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
                createdBy: data.createdBy,
                updatedBy: data.updatedBy,
              };
            }
          });
          if (Object.keys(translations).length > 0) {
            set({ translations, loading: false, error: null });
          } else {
            set({ error: 'No translations found', loading: false });
          }
        },
        error => {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch translations',
            loading: false,
          });
          console.error('Error fetching translations:', error);
        }
      );

      // Initial fetch for immediate data
      const querySnapshot = await getDocs(q);
      const translations: Record<string, IndexTranslation> = {};
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data) {
          translations[doc.id] = {
            indexName: doc.id,
            en: data.en,
            fr: data.fr,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            createdBy: data.createdBy,
            updatedBy: data.updatedBy,
          };
        }
      });

      if (Object.keys(translations).length > 0) {
        set({ translations, loading: false, error: null });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch translations',
        loading: false,
      });
      console.error('Error fetching translations:', error);
    }
  },

  getTranslation: async indexName => {
    try {
      const translations = get().translations;
      if (translations[indexName]) {
        return translations[indexName];
      }

      const docRef = doc(firestore, COLLECTION_NAME, indexName);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const translation: IndexTranslation = {
          indexName: docSnap.id,
          en: data.en,
          fr: data.fr,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          createdBy: data.createdBy,
          updatedBy: data.updatedBy,
        };

        set(state => ({
          translations: {
            ...state.translations,
            [indexName]: translation,
          },
          currentTranslation: translation,
        }));
        return translation;
      }

      return null;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to get translation',
        loading: false,
      });
      console.error('Error getting translation:', error);
      return null;
    }
  },

  subscribeToTranslations: () => {
    const q = query(collection(firestore, COLLECTION_NAME));
    return onSnapshot(
      q,
      querySnapshot => {
        const translations: Record<string, IndexTranslation> = {};
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (data) {
            translations[doc.id] = {
              indexName: doc.id,
              en: data.en,
              fr: data.fr,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              createdBy: data.createdBy,
              updatedBy: data.updatedBy,
            };
          }
        });
        set({ translations });
      },
      error => {
        set({ error: error.message });
        console.error('Error in translation subscription:', error);
      }
    );
  },

  setCurrentTranslation: translation => {
    set({ currentTranslation: translation });
  },
});
